/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {DeletePopUpComponent} from '../../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../../core/state/state.service';
import {GlobalVehicleScenarioMap} from '../../../../models/global-vehicle-scenario-map.model';
import {VehicleScenario} from '../../../../models/vehicle-scenario-dto.model';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';
import {EditVehicleScenarioPopupComponent} from './edit-vehicle-scenario-popup/edit-vehicle-scenario-popup.component';


@Component({
    selector: 'app-manage-vehicle-scenario',
    templateUrl: './manage-vehicle-scenario.component.html',
    styleUrls: ['./manage-vehicle-scenario.component.scss'],
    standalone: false
})
export class ManageVehicleScenarioComponent implements OnInit {
  showSpinner: boolean;
  noData: boolean;
  pageSize: number;

  scenarioColumns: ColDef[] = [
    {field: 'vehScenId', headerName: 'ID', filter: AutocompleteFilterComponent},
    {field: 'vehScenName', headerName: 'Name', filter: AutocompleteFilterComponent},
    {field: 'fcorg', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {
      field: 'industryScenario.industryScenarioName',
      headerName: 'Industry Scenario',
      filter: AutocompleteFilterComponent
    },
    {field: 'isDeleteLocked', headerName: 'Delete Lock', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'Edit Lock', filter: AutocompleteFilterComponent},
    {field: 'isHidden', headerName: 'Hidden', filter: AutocompleteFilterComponent},
    {field: 'createdBy', headerName: 'Created By', filter: AutocompleteFilterComponent},
    {field: 'scenCat', headerName: 'Scenario Category/Forecast Gate', filter: AutocompleteFilterComponent},
    {field: 'scenType', headerName: 'Scenario Type', filter: AutocompleteFilterComponent},
    {field: 'propulsionType.propulsionType', headerName: 'Propulsion Type', filter: AutocompleteFilterComponent},
    {field: 'forecastState.forecastState', headerName: 'Forecast State', filter: AutocompleteFilterComponent},
    {field: 'engProgClass.engProgClass', headerName: 'Engineering Program Class', filter: AutocompleteFilterComponent},
    {field: 'vehScenAsmptn', headerName: 'Assumption', filter: AutocompleteFilterComponent}
  ]

  selectedValue: any;
  selectedId: number;
  vehicleScenarios: VehicleScenario[] = [];
  globalScenarios: GlobalVehicleScenarioMap[] = [];
  scenarioCategories: string[] = [];


  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<VehicleScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.fcorg : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(public snackBar: FabMessageService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              private readonly stateService: StateService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.selectedValue = null;
    this.loadVehicleScenarios();
    this.pageSize = 20;

  }


  /**
   * Fetch all vehicle scenarios from backend endpoint
   */
  loadVehicleScenarios(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getVehicleScenarios(this.stateService.getUserGmid()).subscribe(
      data => {
        this.vehicleScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.showSpinner = false;
        this.noData = (this.vehicleScenarios.length === 0);
      }
    );
  }

  /**
   * Delete a selected vehicle Scenario from DB.
   */
  deleteVehicleScenario(): void {
    this.showSpinner = true;
    const vehicleScenario: VehicleScenario = this.vehicleScenarios.find(is => is.vehScenId === this.selectedId);
    this.vehicleScenarioService.getGlobalVehicleScenarioMapByVehicleScenarioId(this.selectedId).subscribe(
      data => {
        this.globalScenarios = data;
      },
      err => {
        this.errorHandle(err);
        this.showSpinner = false;
      },
      () => {
        if (this.globalScenarios.length > 0) {
          this.snackBar.displayError('The selected Vehicle Scenario is attached to Global ' +
            'Vehicle Scenario, therefore, it cannot be deleted', DISMISS_MSG);
          this.loadVehicleScenarios();
          this.selectedValue = null;
        } else if (!vehicleScenario.isDeleteLocked && this.globalScenarios.length === 0) {
          const dialogRef = this.dialog.open(DeletePopUpComponent, {
            data: {
              title: 'Delete',
              valueToDelete: this.selectedValue
            }
          });
          dialogRef.afterClosed().subscribe(res => {
            // received data from dialog-component
            if (res && res.data.deleteValue) {

              this.vehicleScenarioService.deleteVehicleScenario(this.selectedId).subscribe({
                error: err => {
                  this.errorHandle(err);
                  this.loadVehicleScenarios();
                  this.showSpinner = false;
                },
                complete: () => {
                  this.snackBar.displaySuccess('Successfully removed vehicle scenario', DISMISS_MSG);
                  this.loadVehicleScenarios();
                  this.selectedValue = null;
                }
              });
            }
          });
        } else {
          this.snackBar.displayError('The selected Vehicle Scenario is locked and can not be deleted', DISMISS_MSG);
          this.loadVehicleScenarios();
          this.selectedValue = null;
        }
        this.showSpinner = false;
      }
    );
  }

  /**
   * Navigates to edit vehicle scenario page
   */
  editVehicleScenario(id: number): void {
    const vehicleScenario = this.vehicleScenarios.find(is => is.vehScenId === id);
    this.dialog.open(EditVehicleScenarioPopupComponent, {
      disableClose: true,
      width: '800px',
      data: vehicleScenario
    }).afterClosed().subscribe({
      complete: () => {
        this.loadVehicleScenarios();
        this.selectedValue = null;
      }
    });
  }

  /**
   * Event for selecting a scenario
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.vehScenName);
      this.selectedId = event.data.vehScenId;
    } else if (event.data.vehScenId === this.selectedId) {
      this.selectedValue = null;
      this.selectedId = null;
    }
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = null;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
